import React, { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LanguageIcon from '@mui/icons-material/Language';
import { GoogleMap, Marker, DirectionsRenderer, OverlayView } from '@react-google-maps/api';
import { formatDistanceFrom } from '../../utils';
import type { City, Segment, POI, EnrichedSegment, Restaurant, GoogleMapsLoadedProps } from '../../types';

export type { POI, EnrichedSegment };

interface POIMapProps extends GoogleMapsLoadedProps {
  pois: POI[];
  segments: Segment[];
  selectedDay: string | number;
  cities: City[];
  onPOIClick?: (segment: EnrichedSegment) => void;
  enrichSegmentWithPOI: (segment: Segment) => EnrichedSegment;
}

interface NormalizedPOI extends POI {
  latitude: number;
  longitude: number;
}

function POIMap({
  pois,
  segments,
  selectedDay,
  isLoaded,
  loadError,
  cities,
  onPOIClick,
  enrichSegmentWithPOI
}: POIMapProps) {
  const [filteredPOIs, setFilteredPOIs] = useState<POI[]>([]);
  const [enrichedPOIs, setEnrichedPOIs] = useState<EnrichedSegment[]>([]);
  const [directionsResponse, setDirectionsResponse] = useState<google.maps.DirectionsResult | null>(null);
  const [routeDistance, setRouteDistance] = useState<string | null>(null);
  const [routeDuration, setRouteDuration] = useState<string | null>(null);
  const [directionsError, setDirectionsError] = useState<string | null>(null);
  const [hoveredPOI, setHoveredPOI] = useState<{ poi: NormalizedPOI; index: number } | null>(null);
  const [showRestaurants, setShowRestaurants] = useState(true);
  const [hoveredRestaurant, setHoveredRestaurant] = useState<Restaurant | null>(null);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [lastInitializedDay, setLastInitializedDay] = useState<string | number | null>(null);

  useEffect(() => {
    if (!pois || !segments) return;

    // For specific day, get POI exploration segments in order
    const dayNumber = parseInt(String(selectedDay));
    const dayPOIExplorationSegments = segments
      .filter(segment => parseInt(String(segment.day)) === dayNumber && segment.segment_type === 'poi_exploration')
      .sort((a, b) => (a.position || 0) - (b.position || 0));

    // Build ordered POI list based on segment sequence and enrich them
    const orderedEnrichedPOIs = dayPOIExplorationSegments
      .map(segment => {
        const enriched = enrichSegmentWithPOI(segment);
        return enriched;
      })
      .filter(Boolean);

    // Extract just the POI data for rendering markers
    const orderedPOIs = orderedEnrichedPOIs
      .map(enriched => enriched.fullDetails || enriched)
      .filter(poi => poi && (poi.latitude || poi.lat || poi.Latitude) && (poi.longitude || poi.lng || poi.Longitude));

    // If no POIs found for this day, show all trip POIs as fallback
    if (orderedPOIs.length === 0) {
      const poiExplorationSegments = segments.filter(segment => segment.segment_type === 'poi_exploration');
      const tripPOIIds = poiExplorationSegments.map(segment => segment.poi_id).filter(Boolean);
      const tripPOIs = pois.filter(poi => tripPOIIds.includes(poi.id) && (poi.latitude || poi.lat) && (poi.longitude || poi.lng));
      const enrichedTrip = tripPOIs.map(poi => {
        const segment = poiExplorationSegments.find(s => s.poi_id === poi.id);
        return segment ? enrichSegmentWithPOI(segment) : poi;
      });
      setFilteredPOIs(tripPOIs);
      setEnrichedPOIs(enrichedTrip as EnrichedSegment[]);
    } else {
      setFilteredPOIs(orderedPOIs);
      setEnrichedPOIs(orderedEnrichedPOIs);
    }
  }, [pois, segments, selectedDay, enrichSegmentWithPOI]);

  // Extract restaurants from meal segments for selected day
  const dayRestaurants = useMemo(() => {
    if (!segments || !pois) return [];

    const dayNumber = parseInt(String(selectedDay));

    // Get all segments for the day sorted by position
    const daySegments = segments
      .filter(segment => parseInt(String(segment.day)) === dayNumber)
      .sort((a, b) => (a.position || 0) - (b.position || 0));

    const mealSegments = daySegments.filter(
      segment => segment.segment_type === 'lunch' || segment.segment_type === 'dinner'
    );

    const restaurants: Restaurant[] = [];
    mealSegments.forEach(segment => {
      const restaurantOptionsRaw = segment.meta_data?.restaurant_options || [];
      restaurantOptionsRaw.forEach((option: any) => {
        let poi: POI | undefined = undefined;
        let distance: number | null = null;
        let distanceFrom: string | null = null;

        if (typeof option === 'object' && option.poi_id) {
          poi = pois.find(p => p.id === option.poi_id);
          distance = option.distance_meters;
          distanceFrom = option.distance_from || null;
        } else {
          poi = pois.find(p => p.id === option);
        }

        if (poi && (poi.latitude || poi.lat) && (poi.longitude || poi.lng)) {
          restaurants.push({
            ...poi,
            latitude: poi.latitude || poi.lat,
            longitude: poi.longitude || poi.lng,
            distance_meters: distance,
            distanceFrom: distanceFrom,
            mealType: segment.segment_type,
            restaurantIndex: restaurants.length + 1
          });
        }
      });
    });

    return restaurants;
  }, [segments, pois, selectedDay]);

  // Calculate driving directions for POIs
  useEffect(() => {
    if (!isLoaded || !filteredPOIs || filteredPOIs.length < 2) {
      setDirectionsResponse(null);
      return;
    }

    // Normalize POI coordinates
    const poisWithCoords = filteredPOIs.filter(poi => {
      const hasCoords = (poi.latitude && poi.longitude) ||
                       (poi.lat && poi.lng) ||
                       (poi.Latitude && poi.Longitude);
      return hasCoords;
    });

    const normalizedPOIs: NormalizedPOI[] = poisWithCoords.map(poi => ({
      ...poi,
      latitude: poi.latitude || poi.lat || poi.Latitude || 0,
      longitude: poi.longitude || poi.lng || poi.Longitude || 0
    }));

    if (normalizedPOIs.length < 2) {
      setDirectionsResponse(null);
      return;
    }

    // Calculate driving directions
    const directionsService = new window.google.maps.DirectionsService();

    const origin = { lat: normalizedPOIs[0].latitude, lng: normalizedPOIs[0].longitude };
    const destination = {
      lat: normalizedPOIs[normalizedPOIs.length - 1].latitude,
      lng: normalizedPOIs[normalizedPOIs.length - 1].longitude
    };

    // Create waypoints for intermediate POIs
    // Google Maps allows max 25 total points (23 waypoints + origin + destination)
    const intermediatesPOIs = normalizedPOIs.slice(1, -1);
    const maxWaypoints = 23;

    let waypoints = intermediatesPOIs.map(poi => ({
      location: { lat: poi.latitude, lng: poi.longitude },
      stopover: true
    }));

    // If too many waypoints, limit to first 23
    if (waypoints.length > maxWaypoints) {
      console.warn(`Too many POIs (${waypoints.length}). Limiting to ${maxWaypoints} waypoints for optimal routing.`);
      waypoints = waypoints.slice(0, maxWaypoints);
    }

    directionsService.route(
      {
        origin: origin,
        destination: destination,
        waypoints: waypoints,
        optimizeWaypoints: true, // Let Google optimize the route order
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false, // Use best route only
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK && result) {
          setDirectionsResponse(result);
          setDirectionsError(null);

          // Calculate total distance and duration
          let totalDistance = 0;
          let totalDuration = 0;

          result.routes[0].legs.forEach(leg => {
            totalDistance += leg.distance?.value || 0;
            totalDuration += leg.duration?.value || 0;
          });

          // Convert to km and hours
          setRouteDistance((totalDistance / 1000).toFixed(1));
          setRouteDuration((totalDuration / 3600).toFixed(1));
        } else {
          console.error('POI Directions request failed due to ' + status);
          setDirectionsError('Unable to calculate driving directions between POIs.');
          setDirectionsResponse(null);
        }
      }
    );
  }, [isLoaded, filteredPOIs]);

  if (loadError) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Error loading Google Maps: {loadError.message}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!isLoaded) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!pois || pois.length === 0) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No points of interest available for map display
          </Typography>
          <Box sx={{
            height: { xs: 250, sm: 350, md: 400 },
            width: '100%',
            borderRadius: 2,
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 2
          }}>
            <Typography variant="body2" color="text.secondary">
              No POIs available
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Calculate bounds to fit all POIs
  const poisWithCoords = filteredPOIs.filter(poi => {
    const hasCoords = (poi.latitude && poi.longitude) ||
                     (poi.lat && poi.lng) ||
                     (poi.Latitude && poi.Longitude);
    return hasCoords;
  });

  const normalizedPOIs: NormalizedPOI[] = poisWithCoords.map(poi => ({
    ...poi,
    latitude: poi.latitude || poi.lat || poi.Latitude || 0,
    longitude: poi.longitude || poi.lng || poi.Longitude || 0
  }));

  if (normalizedPOIs.length === 0) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No points of interest with coordinates available for the selected day
          </Typography>
          <Box sx={{
            height: { xs: 250, sm: 350, md: 400 },
            width: '100%',
            borderRadius: 2,
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 2
          }}>
            <Typography variant="body2" color="text.secondary">
              No POIs to display on map
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Calculate bounds to fit all POIs
  const bounds = {
    north: Math.max(...normalizedPOIs.map(poi => poi.latitude)),
    south: Math.min(...normalizedPOIs.map(poi => poi.latitude)),
    east: Math.max(...normalizedPOIs.map(poi => poi.longitude)),
    west: Math.min(...normalizedPOIs.map(poi => poi.longitude))
  };

  const initialCenter = {
    lat: (bounds.north + bounds.south) / 2,
    lng: (bounds.east + bounds.west) / 2
  };

  const latDiff = bounds.north - bounds.south;
  const lngDiff = bounds.east - bounds.west;
  const maxDiff = Math.max(latDiff, lngDiff);

  let initialZoom = 12;
  if (maxDiff > 10) initialZoom = 8;
  else if (maxDiff > 5) initialZoom = 10;
  else if (maxDiff > 2) initialZoom = 11;
  else if (maxDiff > 1) initialZoom = 12;
  else initialZoom = 14;

  // Recenter map when day changes
  const handleMapLoad = (map: google.maps.Map) => {
    setMapInstance(map);
    setLastInitializedDay(selectedDay);
  };

  // Recenter if day changed and map exists
  if (mapInstance && lastInitializedDay !== selectedDay) {
    mapInstance.setCenter(initialCenter);
    mapInstance.setZoom(initialZoom);
    setLastInitializedDay(selectedDay);
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        {segments && cities && selectedDay && (
          <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>
            {(() => {
              const daySegment = segments.find(s => parseInt(String(s.day)) === parseInt(String(selectedDay)));
              if (daySegment && daySegment.city_id) {
                const city = cities.find(c => c.id === daySegment.city_id);
                return city ? city.name : 'Unknown';
              }
              return 'Unknown';
            })()}
          </Typography>
        )}
        {directionsError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {directionsError}
          </Alert>
        )}
        {/* Restaurant toggle */}
        {dayRestaurants.length > 0 && (
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showRestaurants}
                  onChange={(_, checked) => setShowRestaurants(checked)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#2196F3',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#2196F3',
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <RestaurantIcon sx={{ fontSize: 16, color: showRestaurants ? '#2196F3' : 'text.disabled' }} />
                  <Typography variant="body2" color={showRestaurants ? 'text.primary' : 'text.disabled'}>
                    Show restaurants ({dayRestaurants.length})
                  </Typography>
                </Box>
              }
              sx={{ mr: 0 }}
            />
          </Box>
        )}
        <Box sx={{ height: { xs: 250, sm: 350, md: 400 }, width: '100%', borderRadius: 2, overflow: 'hidden' }}>
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={!mapInstance ? initialCenter : undefined}
            zoom={!mapInstance ? initialZoom : undefined}
            onLoad={handleMapLoad}
            options={{
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: true,
              fullscreenControl: true,
            }}
          >
            {/* Show directions renderer if available */}
            {directionsResponse && (
              <DirectionsRenderer
                directions={directionsResponse}
                options={{
                  suppressMarkers: true,
                  polylineOptions: {
                    strokeColor: '#1976d2',
                    strokeOpacity: 0.8,
                    strokeWeight: 5,
                  },
                }}
              />
            )}

            {/* Always show numbered markers for POIs */}
            {normalizedPOIs.map((poi, index) => (
              <Marker
                key={poi.id}
                position={{ lat: poi.latitude, lng: poi.longitude }}
                label={{
                  text: String(index + 1),
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '12px'
                }}
                onClick={() => {
                  if (onPOIClick && enrichedPOIs[index]) {
                    onPOIClick(enrichedPOIs[index]);
                  }
                }}
                onMouseOver={() => setHoveredPOI({ poi, index })}
                onMouseOut={() => setHoveredPOI(null)}
                cursor="pointer"
              />
            ))}

            {/* Restaurant markers with blue color */}
            {showRestaurants && dayRestaurants.map((restaurant, index) => (
              <Marker
                key={`restaurant-${restaurant.id}-${index}`}
                position={{ lat: restaurant.latitude!, lng: restaurant.longitude! }}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 12,
                  fillColor: '#2196F3',
                  fillOpacity: 1,
                  strokeColor: '#1565C0',
                  strokeWeight: 2,
                }}
                label={{
                  text: 'R',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '11px'
                }}
                onClick={() => {
                  if (restaurant.website) {
                    window.open(restaurant.website, '_blank', 'noopener,noreferrer');
                  }
                }}
                onMouseOver={() => setHoveredRestaurant(restaurant)}
                onMouseOut={() => setHoveredRestaurant(null)}
                cursor="pointer"
              />
            ))}

            {/* Custom hover tooltip using OverlayView */}
            {hoveredPOI && (
              <OverlayView
                position={{
                  lat: hoveredPOI.poi.latitude,
                  lng: hoveredPOI.poi.longitude
                }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '45px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    boxShadow: 3,
                    p: 1.5,
                    minWidth: 200,
                    maxWidth: 450,
                    width: 'max-content',
                    pointerEvents: 'none'
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                      lineHeight: 1.4,
                      mb: hoveredPOI.poi.description ? 0.75 : 0
                    }}
                  >
                    {hoveredPOI.poi.name}
                  </Typography>
                  {hoveredPOI.poi.description && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.4,
                        display: 'block'
                      }}
                    >
                      {hoveredPOI.poi.description}
                    </Typography>
                  )}
                </Box>
              </OverlayView>
            )}

            {/* Restaurant hover tooltip */}
            {hoveredRestaurant && (
              <OverlayView
                position={{
                  lat: hoveredRestaurant.latitude!,
                  lng: hoveredRestaurant.longitude!
                }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '45px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: 'white',
                    border: '2px solid #2196F3',
                    borderRadius: 1,
                    boxShadow: 3,
                    p: 1.5,
                    minWidth: 200,
                    maxWidth: 350,
                    width: 'max-content',
                    pointerEvents: 'auto'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <RestaurantIcon sx={{ fontSize: 18, color: '#2196F3' }} />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        lineHeight: 1.4
                      }}
                    >
                      {hoveredRestaurant.name}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      display: 'block',
                      mb: 0.75,
                      textTransform: 'capitalize'
                    }}
                  >
                    {hoveredRestaurant.mealType}
                  </Typography>
                  {hoveredRestaurant.distance_meters != null && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        mb: 0.75
                      }}
                    >
                      {formatDistanceFrom(hoveredRestaurant.distance_meters, hoveredRestaurant.distanceFrom)}
                    </Typography>
                  )}
                  {hoveredRestaurant.website && (
                    <Box
                      component="a"
                      href={hoveredRestaurant.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: 'primary.main',
                        textDecoration: 'none',
                        fontSize: '0.75rem',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <LanguageIcon sx={{ fontSize: 14 }} />
                      Visit website
                    </Box>
                  )}
                </Box>
              </OverlayView>
            )}
          </GoogleMap>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {normalizedPOIs.length} points of interest for day {selectedDay}
          </Typography>
          {routeDistance && routeDuration && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Total Distance: {routeDistance} km • Estimated Driving Time: {routeDuration} hours
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default POIMap;
